import { useQuery, useMutation, useQueryClient } from 'react-query';
import useAuth from '../use-auth';
import { queryOptions } from '../use-hydra';
import {
  getCmsModules,
  getCmsModule,
  createCmsModule,
  updateCmsModule,
  CreateCmsModuleRequest,
  UpdateCmsModuleRequest,
  getCmsProjects,
  getCmsProject,
  createCmsProject,
  updateCmsProject,
  CreateCmsProjectRequest,
  UpdateCmsProjectRequest,
  getCmsAssessmentTests,
  getCmsAssessmentTest,
  createCmsAssessmentTest,
  updateCmsAssessmentTest,
} from '../../api/admin';
import type {
  CreateCmsAssessmentTestRequest,
  UpdateCmsAssessmentTestRequest,
} from '../../schemas/assessment-test';

export function useCmsGetModules() {
  const { getTokenSilently, isAuthenticated } = useAuth();

  return useQuery(
    ['cms-get-modules', isAuthenticated],
    async () => {
      if (!isAuthenticated) return null;
      const token = await getTokenSilently();
      return getCmsModules(token);
    },
    queryOptions
  );
}

export function useCmsGetModule(slug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  return useQuery(
    ['cms-get-module', slug, isAuthenticated],
    async () => {
      if (!isAuthenticated || !slug) return null;
      const token = await getTokenSilently();
      return getCmsModule(token, slug);
    },
    { ...queryOptions, enabled: !!slug }
  );
}

export function useCmsCreateModule() {
  const { getTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: CreateCmsModuleRequest) => {
      const token = await getTokenSilently();
      return createCmsModule(token, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cms-get-modules');
      },
    }
  );
}

export function useCmsUpdateModule(slug: string) {
  const { getTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: UpdateCmsModuleRequest) => {
      const token = await getTokenSilently();
      return updateCmsModule(token, slug, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cms-get-modules');
        queryClient.invalidateQueries(['cms-get-module', slug]);
      },
    }
  );
}

export function useCmsGetProjects() {
  const { getTokenSilently, isAuthenticated } = useAuth();

  return useQuery(
    ['cms-get-projects', isAuthenticated],
    async () => {
      if (!isAuthenticated) return null;
      const token = await getTokenSilently();
      return getCmsProjects(token);
    },
    queryOptions
  );
}

export function useCmsGetProject(slug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  return useQuery(
    ['cms-get-project', slug, isAuthenticated],
    async () => {
      if (!isAuthenticated || !slug) return null;
      const token = await getTokenSilently();
      return getCmsProject(token, slug);
    },
    { ...queryOptions, enabled: !!slug }
  );
}

export function useCmsCreateProject() {
  const { getTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: CreateCmsProjectRequest) => {
      const token = await getTokenSilently();
      return createCmsProject(token, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cms-get-projects');
      },
    }
  );
}

export function useCmsUpdateProject(slug: string) {
  const { getTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: UpdateCmsProjectRequest) => {
      const token = await getTokenSilently();
      return updateCmsProject(token, slug, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cms-get-projects');
        queryClient.invalidateQueries(['cms-get-project', slug]);
      },
    }
  );
}

export function useCmsGetAssessmentTests() {
  const { getTokenSilently, isAuthenticated } = useAuth();

  return useQuery(
    ['cms-get-assessment-tests', isAuthenticated],
    async () => {
      if (!isAuthenticated) return null;
      const token = await getTokenSilently();
      return getCmsAssessmentTests(token);
    },
    queryOptions
  );
}

export function useCmsGetAssessmentTest(slug: string) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  return useQuery(
    ['cms-get-assessment-test', slug, isAuthenticated],
    async () => {
      if (!isAuthenticated || !slug) return null;
      const token = await getTokenSilently();
      return getCmsAssessmentTest(token, slug);
    },
    { ...queryOptions, enabled: !!slug }
  );
}

export function useCmsCreateAssessmentTest() {
  const { getTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: CreateCmsAssessmentTestRequest) => {
      const token = await getTokenSilently();
      return createCmsAssessmentTest(token, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cms-get-assessment-tests');
      },
    }
  );
}

export function useCmsUpdateAssessmentTest(slug: string) {
  const { getTokenSilently } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: UpdateCmsAssessmentTestRequest) => {
      const token = await getTokenSilently();
      return updateCmsAssessmentTest(token, slug, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cms-get-assessment-tests');
        queryClient.invalidateQueries(['cms-get-assessment-test', slug]);
      },
    }
  );
}
